// PRE RMA CSB — Stockage local (IndexedDB), point 28 : la saisie ET la
// modification doivent continuer a fonctionner sans Internet, sans aucune
// dependance externe.
//
// Deux object stores :
//  - 'consultations'       : file d'attente des operations non encore
//                             confirmees par le serveur (CREATE ou UPDATE).
//  - 'consultations_cache' : dernier etat connu des consultations SYNCED,
//                             recu du serveur, pour un affichage en lecture
//                             hors connexion (Saisie Unique / RMA / Historique).

const DB_NAME = 'pre_rma_csb';
const DB_VERSION = 4;
const STORE_QUEUE = 'consultations';
const STORE_CACHE = 'consultations_cache';
const STORE_ABONNEMENT = 'abonnement';
const STORE_TOTAUX = 'totaux_locaux';

export type SyncStatus = 'PENDING' | 'SYNCED' | 'ERROR' | 'CONFLICT';
// DELETE_ALL = « Supprimer tout » : ordre unique « supprimer toutes les consultations de la période » (rejoué dès le retour du réseau).
export type Operation = 'CREATE' | 'UPDATE' | 'DELETE' | 'DELETE_ALL';

export interface LocalConsultation {
  localId: string;             // genere cote client (crypto.randomUUID())
  operation: Operation;
  periodeId: string;
  csbId: string;
  consultationId?: string;     // id serveur — requis pour UPDATE et DELETE
  baseVersion?: number;        // version de depart — requis pour UPDATE et DELETE (point 30)
  payload: Record<string, unknown>; // corps exact envoye au serveur
  syncStatus: SyncStatus;
  serverId?: string;
  serverVersion?: number;
  erreur?: string;
  versionServeurConflit?: number; // renseigne si syncStatus === 'CONFLICT'
  codeHttp?: number;              // code HTTP du refus définitif (400, 422, 500…), renseigné si syncStatus === 'ERROR'
  refuseLe?: number;              // horodatage du refus
  createdAt: number;
  updatedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const store = db.createObjectStore(STORE_QUEUE, { keyPath: 'localId' });
        store.createIndex('by_sync_status', 'syncStatus');
        store.createIndex('by_periode', 'periodeId');
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        const cache = db.createObjectStore(STORE_CACHE, { keyPath: 'id' });
        cache.createIndex('by_periode', 'periodeId');
      }
      // v3 : dernier état d'abonnement connu (un enregistrement par CSB), pour décider des
      // modules accessibles SANS réseau. Ne touche pas aux deux autres stores.
      if (!db.objectStoreNames.contains(STORE_ABONNEMENT)) {
        db.createObjectStore(STORE_ABONNEMENT, { keyPath: 'csbId' });
      }
      // v4 : derniers totaux (RMA RCE) calculés LOCALEMENT pour chaque période — un enregistrement par période, remplacé à chaque calcul.
      if (!db.objectStoreNames.contains(STORE_TOTAUX)) {
        db.createObjectStore(STORE_TOTAUX, { keyPath: 'periodeId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Une opération, une transaction. Le résultat n'est rendu qu'APRÈS la validation (commit) de la transaction : quand `await` rend la main,
 * la donnée est réellement écrite sur l'appareil (avant : on rendait la main dès la réussite de la requête, avant le commit).
 */
async function withStore<T>(storeName: string, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let resultat: T;
    let tx: IDBTransaction;
    try {
      tx = db.transaction(storeName, mode);
      const req = fn(tx.objectStore(storeName));
      req.onsuccess = () => { resultat = req.result; };
      req.onerror = () => { db.close(); reject(req.error); };
    } catch (err) { db.close(); reject(err); return; }
    tx.oncomplete = () => { db.close(); resolve(resultat); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Opération locale annulée')); };
  });
}

// --- File d'attente (CREATE / UPDATE en cours) ---

export async function sauvegarderLocalement(item: LocalConsultation): Promise<void> {
  await withStore(STORE_QUEUE, 'readwrite', (store) => store.put(item));
}

/** Écrit TOUT un lot dans la file locale en UNE transaction : tout est enregistré, ou rien (import massif, 100 % hors connexion). */
export async function sauvegarderLotLocalement(items: LocalConsultation[]): Promise<void> {
  if (!items.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    const store = tx.objectStore(STORE_QUEUE);
    for (const item of items) store.put(item);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Écriture locale annulée')); };
  });
}

export async function listerEnAttente(): Promise<LocalConsultation[]> {
  const all = await withStore<LocalConsultation[]>(STORE_QUEUE, 'readonly', (store) => store.getAll());
  return all.filter((i) => i.syncStatus !== 'SYNCED');
}

export async function listerParPeriode(periodeId: string): Promise<LocalConsultation[]> {
  const all = await withStore<LocalConsultation[]>(STORE_QUEUE, 'readonly', (store) => store.getAll());
  return all.filter((i) => i.periodeId === periodeId);
}

export async function obtenirEnAttente(localId: string): Promise<LocalConsultation | undefined> {
  return withStore<LocalConsultation>(STORE_QUEUE, 'readonly', (store) => store.get(localId));
}

export async function marquerSynchronise(localId: string, serverId: string, serverVersion: number): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'SYNCED', serverId, serverVersion, updatedAt: Date.now() });
}

export async function marquerErreur(localId: string, erreur: string): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'ERROR', erreur, updatedAt: Date.now() });
}

/**
 * REFUS DÉFINITIF du serveur (400/422… ou crash 500) : la saisie reste dans IndexedDB, statut ERROR affiché « ⛔ Refusé / À vérifier »,
 * avec la mention « Refusé » + code HTTP + motif. Rien n'est perdu et la file passe tout de suite à la saisie suivante.
 */
export async function marquerRefuse(localId: string, motif: string, codeHttp?: number): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  const entete = `Refusé${codeHttp ? ` (code ${codeHttp})` : ''} : `;
  await sauvegarderLocalement({ ...item, syncStatus: 'ERROR', erreur: entete + motif, codeHttp, refuseLe: Date.now(), updatedAt: Date.now() });
}

/** Point 30 : jamais d'écrasement silencieux — le serveur gagne (décision validée). */
export async function marquerConflit(localId: string, versionServeur: number, message: string): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'CONFLICT', versionServeurConflit: versionServeur, erreur: message, updatedAt: Date.now() });
}

/** Modifie le contenu d'une opération encore PENDING (avant tout envoi reseau). */
export async function modifierEnAttente(localId: string, payload: Record<string, unknown>): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, payload: { ...item.payload, ...payload }, updatedAt: Date.now() });
}

// Point 28 (IMPORTANT) : on ne supprime JAMAIS un enregistrement local tant
// que le statut n'est pas SYNCED avec confirmation serveur. Purge manuelle
// uniquement (ex: l'utilisateur abandonne une saisie en ERROR), jamais
// appelee automatiquement par la file de synchronisation elle-meme.
export async function supprimerLocal(localId: string): Promise<void> {
  await withStore(STORE_QUEUE, 'readwrite', (store) => store.delete(localId));
}

/**
 * « Supprimer tout » : vide EN UNE transaction toutes les données locales de consultations d'une période — file d'attente
 * (CREATE/UPDATE/DELETE en attente, en erreur, en conflit ou déjà SYNCED) ET cache de lecture. Seuls les ordres DELETE_ALL sont conservés
 * (ils doivent encore partir vers le serveur). Ne touche ni au CPN ni aux modules (autres bases/stores). Retourne le nombre d'éléments retirés.
 * 100 % LOCAL : aucun appel réseau, donc utilisable tel quel quand le serveur est injoignable ou répond 401/403 (purge de secours).
 */
export async function purgerConsultationsPeriode(periodeId: string, retirerOrdresNonEnvoyables = false): Promise<number> {
  const db = await openDb();
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction([STORE_QUEUE, STORE_CACHE], 'readwrite');
    const queue = tx.objectStore(STORE_QUEUE);
    const cache = tx.objectStore(STORE_CACHE);
    let retires = 0;
    const lectureFile = queue.getAll();
    lectureFile.onsuccess = () => {
      for (const i of lectureFile.result as LocalConsultation[]) {
        // `retirerOrdresNonEnvoyables` (purge de secours) : les ordres « Supprimer tout » déjà REFUSÉS/en erreur (ex. « Session invalide ou
        // expirée » d'une ancienne version) n'ont plus rien à envoyer et encombrent l'écran de synchronisation → retirés aussi.
        // Un ordre encore PENDING est TOUJOURS conservé : il partira au serveur dès que possible.
        const ordreInutile = retirerOrdresNonEnvoyables && i.operation === 'DELETE_ALL' && i.syncStatus !== 'PENDING';
        if (i.periodeId === periodeId && (i.operation !== 'DELETE_ALL' || ordreInutile)) { queue.delete(i.localId); retires++; }
      }
    };
    const lectureCache = cache.getAll();
    lectureCache.onsuccess = () => {
      for (const c of lectureCache.result as Array<{ id: string; periodeId?: string }>) {
        if (c.periodeId === periodeId) { cache.delete(c.id); retires++; }
      }
    };
    tx.oncomplete = () => { db.close(); resolve(retires); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Purge locale annulée')); };
  });
}

/** Vide uniquement le cache de lecture d'une période (après confirmation serveur d'un « Supprimer tout »). */
export async function purgerCachePeriode(periodeId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    const cache = tx.objectStore(STORE_CACHE);
    const lecture = cache.getAll();
    lecture.onsuccess = () => {
      for (const c of lecture.result as Array<{ id: string; periodeId?: string }>) if (c.periodeId === periodeId) cache.delete(c.id);
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Purge du cache annulée')); };
  });
}

// --- Cache en lecture des consultations déjà synchronisées ---

export async function mettreEnCacheConsultations(periodeId: string, consultations: Array<Record<string, unknown> & { id: string }>): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    const store = tx.objectStore(STORE_CACHE);
    const presents = new Set(consultations.map((c) => c.id));
    // La liste reçue est COMPLÈTE pour la période : un patient absent a été supprimé (ici ou sur un autre appareil) → on le retire
    // du cache, sinon il réapparaîtrait dans la liste hors connexion.
    const lecture = store.getAll();
    lecture.onsuccess = () => {
      for (const c of lecture.result as Array<{ id: string; periodeId?: string }>) {
        if (c.periodeId === periodeId && !presents.has(c.id)) store.delete(c.id);
      }
      for (const c of consultations) store.put({ ...c, periodeId });
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => reject(tx.error);
  });
}

/** Retire un patient du cache de lecture (suppression demandée : il ne doit plus s'afficher, même hors connexion). */
export async function supprimerDuCache(consultationId: string): Promise<void> {
  await withStore(STORE_CACHE, 'readwrite', (store) => store.delete(consultationId));
}

export async function chargerConsultationsCache(periodeId: string): Promise<Array<Record<string, unknown>>> {
  const all = await withStore<Array<Record<string, unknown> & { periodeId: string }>>(STORE_CACHE, 'readonly', (store) => store.getAll());
  return all.filter((c) => c.periodeId === periodeId);
}

// --- Abonnement : état en cache pour le mode hors connexion ---

export interface AbonnementLocal {
  csbId: string;
  recuLe: number; // horodatage de la dernière réponse du serveur
  abonnement: Record<string, unknown>; // réponse de GET /api/abonnements/:csbId, telle que reçue
}

export async function enregistrerAbonnementLocal(csbId: string, abonnement: Record<string, unknown>): Promise<void> {
  await withStore(STORE_ABONNEMENT, 'readwrite', (store) => store.put({ csbId, recuLe: Date.now(), abonnement } satisfies AbonnementLocal));
}

export async function lireAbonnementLocal(csbId: string): Promise<AbonnementLocal | undefined> {
  return withStore<AbonnementLocal | undefined>(STORE_ABONNEMENT, 'readonly', (store) => store.get(csbId));
}

/** Déconnexion (poste partagé) : l'état d'abonnement d'un CSB ne doit pas rester sur l'appareil. */
export async function effacerAbonnementLocal(): Promise<void> {
  await withStore(STORE_ABONNEMENT, 'readwrite', (store) => store.clear());
}

// --- Totaux calculés localement (RMA RCE) : disponibles hors connexion, même quand le serveur est injoignable ---

/** Instantané des totaux d'une période (forme : TotauxLocaux de shared/business-logic/calculLocal.ts). */
export interface TotauxLocauxStockes { periodeId: string; csbId: string; totaux: Record<string, unknown> }

export async function enregistrerTotauxLocaux(periodeId: string, csbId: string, totaux: Record<string, unknown>): Promise<void> {
  await withStore(STORE_TOTAUX, 'readwrite', (store) => store.put({ periodeId, csbId, totaux } satisfies TotauxLocauxStockes));
}

export async function lireTotauxLocaux(periodeId: string): Promise<TotauxLocauxStockes | undefined> {
  return withStore<TotauxLocauxStockes | undefined>(STORE_TOTAUX, 'readonly', (store) => store.get(periodeId));
}

/** Déconnexion (poste partagé) : les totaux d'un CSB ne restent pas sur l'appareil. */
export async function effacerTotauxLocaux(): Promise<void> {
  await withStore(STORE_TOTAUX, 'readwrite', (store) => store.clear());
}
